// Date and display helpers for the Planner widget (components/widgets/Planner.tsx).
// Planner dates are instants (ISO, UTC); Planner shows them as the viewer's local day, so
// every view works in local days too.

import type { PlannerBoard, PlannerCard } from '../services/api';
import type { GanttPeriod } from '../store/settings';
import { addDays, startOfWeek } from './calendarRange';

/** Whole local days from b to a. */
const dayDiff = (a: Date, b: Date) => Math.round((a.getTime() - b.getTime()) / 86_400_000);

/** Planner's 25 label colours, by slot ("category1" = pink … "category25" = dark grey). */
const LABEL_COLORS = [
  '#ec4899', '#ef4444', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#b45309', '#84cc16', '#06b6d4', '#6b7280',
  '#cbd5e1', '#92400e', '#be123c', '#f97316', '#fdba74', '#f59e0b', '#86efac', '#15803d', '#14b8a6', '#7dd3fc',
  '#1e40af', '#c4b5fd', '#86198f', '#d1d5db', '#374151',
];

export function labelColor(slot: string): string {
  const n = Number(slot.replace(/^category/, ''));
  return LABEL_COLORS[(Number.isInteger(n) && n >= 1 && n <= 25 ? n : 10) - 1]!;
}

/** Black or white, whichever reads better on `hex`. */
export function textOn(hex: string): string {
  const v = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#000000' : '#ffffff';
}

/** The local day (midnight) an instant falls on. */
export function localDay(iso: string): Date {
  const d = new Date(iso);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export const today = (now = new Date()): Date => new Date(now.getFullYear(), now.getMonth(), now.getDate());

export function isOverdue(t: PlannerCard, now = new Date()): boolean {
  return t.progress !== 'completed' && Boolean(t.due) && localDay(t.due!) < today(now);
}

/** Tasks to draw: completed ones only when asked for. */
export function shownTasks(board: PlannerBoard, showCompleted: boolean): PlannerCard[] {
  return showCompleted ? board.tasks : board.tasks.filter((t) => t.progress !== 'completed');
}

const PRIORITY_RANK = { urgent: 0, important: 1, medium: 2, low: 3 } as const;

/** List order: open before done, then by due date (none last), then priority. Planner order breaks ties. */
export function listOrder(tasks: PlannerCard[]): PlannerCard[] {
  return tasks
    .map((t, i) => ({ t, i }))
    .sort((a, b) => {
      const done = Number(a.t.progress === 'completed') - Number(b.t.progress === 'completed');
      if (done) return done;
      const ad = a.t.due ? new Date(a.t.due).getTime() : Infinity;
      const bd = b.t.due ? new Date(b.t.due).getTime() : Infinity;
      if (ad !== bd) return ad < bd ? -1 : 1;
      return PRIORITY_RANK[a.t.priority] - PRIORITY_RANK[b.t.priority] || a.i - b.i;
    })
    .map((x) => x.t);
}

/**
 * Gantt (waterfall) order: earliest start first – a task with only a due date counts from
 * that date – then earliest due date. Undated tasks go last; Planner order breaks ties.
 */
export function ganttOrder(tasks: PlannerCard[]): PlannerCard[] {
  const time = (iso: string | null) => (iso ? new Date(iso).getTime() : Infinity);
  return tasks
    .map((t, i) => ({ t, i, from: time(t.start ?? t.due), to: time(t.due) }))
    .sort((a, b) => (a.from !== b.from ? (a.from < b.from ? -1 : 1) : a.to !== b.to ? (a.to < b.to ? -1 : 1) : a.i - b.i))
    .map((x) => x.t);
}

export type GanttRow<T> = { kind: 'bucket'; id: string; name: string } | { kind: 'task'; id: string; t: PlannerCard; bar: T };

/**
 * The Gantt chart's rows: tasks in waterfall order (ganttOrder), either as one list or under
 * bucket headings in the board's order (the Kanban view's columns, left to right), each
 * bucket in waterfall order too. Tasks in no known bucket go under "Other", last. Tasks
 * `place` returns null for (no dates, or outside the period) are left out.
 */
export function ganttRows<T>(board: PlannerBoard, tasks: PlannerCard[], group: boolean, place: (t: PlannerCard) => T | null): Array<GanttRow<T>> {
  const placed = ganttOrder(tasks).flatMap((t) => {
    const bar = place(t);
    return bar === null ? [] : [{ kind: 'task' as const, id: t.id, t, bar }];
  });
  if (!group) return placed;
  const known = new Set(board.buckets.map((b) => b.id));
  const bucketOf = (t: PlannerCard) => (t.bucketId && known.has(t.bucketId) ? t.bucketId : '');
  return [...board.buckets, { id: '', name: 'Other' }].flatMap((b) => {
    const mine = placed.filter((r) => bucketOf(r.t) === b.id);
    return mine.length ? [{ kind: 'bucket' as const, id: `b-${b.id}`, name: b.name }, ...mine] : [];
  });
}

export type DueGroup = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none' | 'done';

export const DUE_GROUP_LABELS: Record<DueGroup, string> = {
  overdue: 'Late',
  today: 'Today',
  tomorrow: 'Tomorrow',
  week: 'Next 7 days',
  later: 'Later',
  none: 'No date',
  done: 'Completed',
};

export function dueGroup(t: PlannerCard, now = new Date()): DueGroup {
  if (t.progress === 'completed') return 'done';
  if (!t.due) return 'none';
  const d = localDay(t.due);
  const t0 = today(now);
  if (d < t0) return 'overdue';
  if (d.getTime() === t0.getTime()) return 'today';
  if (d.getTime() === addDays(t0, 1).getTime()) return 'tomorrow';
  return d < addDays(t0, 8) ? 'week' : 'later';
}

/** Groups in a fixed order, leaving out empty ones. */
export function groupBy<K extends string>(tasks: PlannerCard[], keyOf: (t: PlannerCard) => K, order: K[]): Array<{ key: K; tasks: PlannerCard[] }> {
  const map = new Map<K, PlannerCard[]>();
  for (const t of tasks) {
    const k = keyOf(t);
    map.set(k, [...(map.get(k) ?? []), t]);
  }
  return order.filter((k) => map.has(k)).map((k) => ({ key: k, tasks: map.get(k)! }));
}

export interface GanttRange {
  first: Date;
  days: number;
}

export interface GanttWindowOptions {
  period: GanttPeriod;
  /** Set period: "YYYY-MM-DD", both included. */
  from: string;
  to: string;
  /** Rolling: how many units before and after today. */
  before: number;
  after: number;
  unit: 'days' | 'weeks' | 'months';
  weekStartsOn: 0 | 1;
}

/** Longest timeline drawn (about three years), whatever the dates. */
export const MAX_GANTT_DAYS = 1100;

const parseDay = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : null;
};

const shift = (d: Date, n: number, unit: GanttWindowOptions['unit']): Date => {
  if (unit === 'days') return addDays(d, n);
  if (unit === 'weeks') return addDays(d, n * 7);
  const r = new Date(d.getFullYear(), d.getMonth() + n, 1);
  // Same day of the month, or the month's last day (31 Jan + 1 month = 28/29 Feb).
  r.setDate(Math.min(d.getDate(), new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate()));
  return r;
};

const span = (first: Date, endExclusive: Date): GanttRange => ({
  first,
  days: Math.min(MAX_GANTT_DAYS, Math.max(1, dayDiff(endExclusive, first))),
});

/**
 * The days the Gantt chart covers:
 *   all     – earliest to latest task date, in whole weeks;
 *   auto    – from the start of last week to the end of the last open task, 4–13 weeks;
 *   period  – the chosen From and To dates (both included);
 *   rolling – N days/weeks/months before today to N after (today included).
 * Missing or invalid dates fall back to auto.
 */
export function ganttWindow(tasks: PlannerCard[], o: GanttWindowOptions, now = new Date()): GanttRange {
  const t0 = today(now);
  const starts = tasks.flatMap((t) => (t.start || t.due ? [localDay((t.start ?? t.due)!)] : []));
  const ends = tasks.flatMap((t) => (t.start || t.due ? [addDays(localDay((t.due ?? t.start)!), 1)] : []));
  if (o.period === 'all' && starts.length) {
    const first = startOfWeek(new Date(Math.min(...starts.map(Number))), o.weekStartsOn);
    const last = new Date(Math.max(...ends.map(Number)));
    return span(first, addDays(first, Math.ceil(dayDiff(last, first) / 7) * 7));
  }
  if (o.period === 'period') {
    const from = parseDay(o.from);
    const to = parseDay(o.to);
    if (from && to && to >= from) return span(from, addDays(to, 1));
  }
  if (o.period === 'rolling') {
    const before = Math.max(0, Math.floor(o.before) || 0);
    const after = Math.max(0, Math.floor(o.after) || 0);
    return span(shift(t0, -before, o.unit), addDays(shift(t0, after, o.unit), 1));
  }
  // auto
  const first = addDays(startOfWeek(t0, o.weekStartsOn), -7);
  const open = tasks.filter((t) => t.progress !== 'completed' && (t.start || t.due));
  const lastOpen = open.length ? Math.max(...open.map((t) => addDays(localDay((t.due ?? t.start)!), 1).getTime())) : 0;
  const weeks = Math.min(13, Math.max(4, Math.ceil((lastOpen - first.getTime()) / (7 * 86_400_000))));
  return { first, days: weeks * 7 };
}

export interface GanttTicks {
  /** Day offsets of grid lines; `strong` ones start a week or month. */
  lines: Array<{ at: number; strong: boolean }>;
  /** Weekend days to shade (short timelines only). */
  weekends: number[];
  labels: Array<{ at: number; text: string }>;
}

/**
 * Grid lines and labels that stay readable at any length: days with weekly labels up to six
 * weeks, then weeks, then months – labelling every second, third… one when there are many.
 */
export function ganttTicks(range: GanttRange, weekStartsOn: 0 | 1): GanttTicks {
  const days = Array.from({ length: range.days }, (_, i) => addDays(range.first, i));
  const weekStarts = days.flatMap((d, i) => (d.getDay() === weekStartsOn ? [i] : []));
  const monthStarts = days.flatMap((d, i) => (d.getDate() === 1 ? [i] : []));
  const dayMonth = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });
  const every = <T,>(list: T[], max: number) => list.filter((_, i) => i % Math.max(1, Math.ceil(list.length / max)) === 0);

  if (range.days <= 45) {
    return {
      lines: days.map((_, i) => ({ at: i, strong: weekStarts.includes(i) })),
      weekends: days.flatMap((d, i) => (d.getDay() === 0 || d.getDay() === 6 ? [i] : [])),
      labels: weekStarts.map((i) => ({ at: i, text: dayMonth.format(days[i]!) })),
    };
  }
  if (range.days <= 200) {
    return {
      lines: weekStarts.map((i) => ({ at: i, strong: monthStarts.some((m) => m >= i && m < i + 7) })),
      weekends: [],
      labels: every(weekStarts, 12).map((i) => ({ at: i, text: dayMonth.format(days[i]!) })),
    };
  }
  // The year goes on the first label and on each January ("Sep 2026 … Dec, Jan 2027"), not as
  // "Oct 26", which reads like a date.
  const month = new Intl.DateTimeFormat(undefined, { month: 'short' });
  const monthYear = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' });
  return {
    lines: monthStarts.map((i) => ({ at: i, strong: days[i]!.getMonth() === 0 })),
    weekends: [],
    labels: every(monthStarts, 12).map((i, n) => ({ at: i, text: (n === 0 || days[i]!.getMonth() === 0 ? monthYear : month).format(days[i]!) })),
  };
}

export interface GanttBar {
  /** Day offsets from the range start; `end` is exclusive. Clipped to the range. */
  from: number;
  to: number;
  /** Only a due date: drawn as a diamond on that day. */
  milestone: boolean;
  /** The task carries on before / after the visible range. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

/** Where a task sits on the timeline, or null if it has no dates or falls outside the range. */
export function ganttBar(t: PlannerCard, range: GanttRange): GanttBar | null {
  if (!t.start && !t.due) return null;
  const milestone = !t.start;
  const s = localDay(t.start ?? t.due!);
  // A start date on its own lasts one day; start and due cover both days.
  const e = addDays(t.due ? localDay(t.due) : s, 1);
  const lo = dayDiff(s, range.first);
  const hi = dayDiff(e < s ? addDays(s, 1) : e, range.first);
  if (hi <= 0 || lo >= range.days) return null;
  return { from: Math.max(0, lo), to: Math.min(range.days, hi), milestone, clippedStart: lo < 0, clippedEnd: hi > range.days };
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0]![0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '')).toUpperCase();
}

/** A steady colour per person, so the same face has the same colour on every card. */
export function personColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 55% 45%)`;
}
